import { motion } from 'framer-motion';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { device } from '../../styles/devices';
import { color, gutter } from '../../styles/mixins';
import { m28 } from '../../styles/typography';
import { Button } from '../button/button';
import { IconButton } from '../button/icon-button';
import CloseSvg from '../svg/close';

const Wrap = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9;

  display: flex;
  justify-content: center;
  align-items: flex-end;

  @media ${device.tablet} {
    align-items: center;
  }
`;

const Host = styled(motion.div)`
  position: relative;
  z-index: 1;
  max-width: 500px;

  margin: ${gutter(2)} ${gutter(2)};

  background-color: ${color('white')};
  filter: drop-shadow(0px 8px 24px rgba(0, 0, 0, 0.1));
  border-radius: ${(props) => props.theme.border.radius}px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${gutter(5)} ${gutter(4)} 28px;
`;

const Title = styled.div`
  ${m28};
`;

const Content = styled.div`
  padding: 0 ${gutter(4)} ${gutter(5)};

  border-bottom: 1px solid ${color('gray30')};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${gutter(4)} ${gutter(4)} ${gutter(2)};

  > * {
    margin-bottom: ${gutter(2)};
  }
`;

const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
`;

interface ModalProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onMount?: () => void;
  action?: React.ReactNode;
}

export const Modal = ({
  title,
  children,
  onClose,
  onMount,
  action,
}: ModalProps) => {
  const titleId = 'title';
  const contentId = 'content';

  const closeOnEscapePress = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', closeOnEscapePress);
    onMount && onMount();
    return () => window.removeEventListener('keydown', closeOnEscapePress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrap>
      <Host
        initial={{ opacity: 0, translateY: 350 }}
        animate={{ opacity: 1, translateY: 0 }}
        exit={{ opacity: 0, translateY: 350 }}
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={contentId}
        aria-modal
        tabIndex={-1}
      >
        <Header>
          <Title id={titleId}>{title}</Title>{' '}
          <IconButton variant="tertiary" onClick={onClose}>
            <CloseSvg />
          </IconButton>
        </Header>
        <Content id={contentId}>{children}</Content>
        <Actions>
          {action}
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
        </Actions>
      </Host>
      <Overlay onMouseDown={() => onClose()} />
    </Wrap>
  );
};
